// src/components/Navbar.js
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { gsap } from 'gsap';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { useCurrency } from '../../context/CurrencyContext';
import { useContent } from '../../context/ContentContext';
import { fadeIn } from '../../utils/gsapAnimations';
import SectionLoader from '../common/SectionLoader';

function Navbar() {
  const navRef = useRef(null);
  const menuRef = useRef(null);
  const cartRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [scrolled, setScrolled] = useState(false);
  const { cart } = useCart();
  const { user, logout } = useAuth();
  const currencyContext = useCurrency();
  const { currency, toggleCurrency } = currencyContext || {};
  const { content, loading: contentLoading } = useContent();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    fadeIn(navRef.current, 0.4);
  }, []);

  useEffect(() => {
    if (cart.length > 0 && cartRef.current) {
      gsap.to(cartRef.current, {
        scale: 1.25,
        duration: 0.18,
        yoyo: true,
        repeat: 1,
        ease: 'power1.out'
      });
    }
  }, [cart.length]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
    if (!isOpen && menuRef.current) {
      gsap.fromTo(
        menuRef.current,
        { opacity: 0, y: -30 },
        { opacity: 1, y: 0, duration: 0.55, ease: 'power3.out' }
      );
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const handleSearch = (e) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      navigate(`/shop?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  };

  // ────────────────────────────────────────────────
  // FIXED: Use ONE consistent env variable for ALL images
  // Put this in .env: REACT_APP_API_BASE=http://localhost:8000
  // ────────────────────────────────────────────────
  const API_BASE = process.env.REACT_APP_IMAGE_URL || 'http://localhost:8000';

  const brandLogo = content?.settings?.brand_logo
    ? `${API_BASE}${content.settings.brand_logo.startsWith('/') ? '' : '/'}${content.settings.brand_logo}`
    : null;

  // Debug – remove after logo works
  console.log('[Navbar] Content loading:', contentLoading);
  console.log('[Navbar] Raw backend path:', content?.settings?.brand_logo);
  console.log('[Navbar] Full logo URL:', brandLogo);
  console.log('[Navbar] Env base:', process.env.REACT_APP_API_BASE);

  const isActive = (path) => location.pathname === path;

  return (
    <nav
      ref={navRef}
      className={`sticky top-0 z-50 bg-white/95 backdrop-blur-xl border-b border-gray-100/80 transition-all duration-300 ${
        scrolled ? 'shadow-md' : 'shadow-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 sm:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group h-12 sm:h-16 lg:h-20 min-w-[160px] sm:min-w-[180px]">
  {contentLoading ? (
    <div className="w-40">
      <SectionLoader message="..." />
    </div>
  ) : brandLogo ? (
    <img
      src={brandLogo}
      alt="Madisa Farm Logo"
      className="h-10 sm:h-14 lg:h-16 w-auto object-contain transition-all duration-400 group-hover:scale-105 group-active:scale-95"
    />
  ) : (
    <span className="text-2xl sm:text-3xl lg:text-4xl font-playfair-display font-bold text-primary tracking-tight group-hover:text-primary/80 transition-colors">
      {content?.settings?.brand_name || 'Madisa Farm'}
    </span>
  )}
</Link>
          {/* Desktop Navigation + Search */}
          <div className="hidden lg:flex items-center gap-10">
            <div className="flex items-center gap-10">
              {[
                { path: '/shop', label: 'Store' },
                { path: '/about', label: 'About' },
                { path: '/contact', label: 'Contact' },
              ].map(({ path, label }) => (
                <Link
                  key={path}
                  to={path}
                  className={`relative text-base font-montserrat transition-all duration-300 after:absolute after:bottom-[-6px] after:left-0 after:h-0.5 after:bg-primary after:transition-all after:duration-300 ${
                    isActive(path)
                      ? 'text-primary font-semibold after:w-full'
                      : 'text-secondary hover:text-primary hover:after:w-full after:w-0'
                  }`}
                >
                  {label}
                </Link>
              ))}
            </div>

            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleSearch}
                placeholder="Search menu..."
                className="w-72 pl-11 pr-5 py-3 rounded-full bg-gray-50/80 border border-gray-200 text-base font-montserrat placeholder:text-gray-400 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 focus:bg-white transition-all duration-300 shadow-inner"
                aria-label="Search menu"
              />
              <svg
                className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          {/* Right-side actions */}
          <div className="flex items-center gap-5 sm:gap-7">
            {/* {currencyContext && (
              <button
                onClick={toggleCurrency}
                className="text-base font-montserrat text-secondary hover:text-primary transition-colors duration-300 px-3 py-2 rounded-lg hover:bg-gray-50"
                aria-label="Toggle currency"
              >
                {currency || 'NGN'}
              </button>
            )} */}

            <Link to="/cart" className="relative group">
              <div className="flex items-center gap-2">
                <svg className="w-6 h-6 text-secondary group-hover:text-primary transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                {cart.length > 0 && (
                  <span
                    ref={cartRef}
                    className="absolute -top-2 -right-2 bg-gradient-to-br from-primary to-primary/80 text-cream text-xs font-bold min-w-[22px] h-5.5 flex items-center justify-center rounded-full shadow-md px-1.5"
                  >
                    {cart.length}
                  </span>
                )}
              </div>
            </Link>

            {user ? (
              <div className="relative group">
                <button className="flex items-center gap-3 text-secondary hover:text-primary transition-colors">
                  <span className="hidden sm:block font-montserrat text-base font-medium">{user.name}</span>
                  <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden border-2 border-gray-200 group-hover:border-primary/60 transition-colors">
                    {user.avatar ? (
                      <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" />
                    ) : (
                      <svg className="w-5 h-5 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
                      </svg>
                    )}
                  </div>
                </button>

                <div className="absolute right-0 mt-3 w-56 bg-white rounded-xl shadow-2xl py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform scale-95 group-hover:scale-100 origin-top-right z-50 border border-gray-100">
                  <Link
                    to="/dashboard"
                    className="block px-5 py-3 text-sm font-montserrat text-secondary hover:bg-gray-50 hover:text-primary transition-colors"
                  >
                    Dashboard
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-5 py-3 text-sm font-montserrat text-secondary hover:bg-gray-50 hover:text-primary transition-colors"
                  >
                    Logout
                  </button>
                </div>
              </div>
            ) : (
              <div className="hidden sm:flex items-center gap-5">
                <Link
                  to="/login"
                  className="text-base font-montserrat text-secondary hover:text-primary transition-colors px-4 py-2 rounded-lg hover:bg-gray-50"
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="bg-primary text-cream px-6 py-3 text-base font-montserrat rounded-full hover:bg-primary/90 transition-all duration-300 shadow-sm hover:shadow-md"
                >
                  Register
                </Link>
              </div>
            )}

            <button
              onClick={toggleMenu}
              className="lg:hidden text-secondary p-2 rounded-full hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/40"
              aria-label="Toggle menu"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div
          ref={menuRef}
          className="lg:hidden bg-white/95 backdrop-blur-lg border-t border-gray-100 shadow-2xl"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-8">
            <Link
              to="/shop"
              className="block text-xl font-montserrat text-secondary hover:text-primary transition-colors"
              onClick={toggleMenu}
            >
              Store
            </Link>
            <Link
              to="/about"
              className="block text-xl font-montserrat text-secondary hover:text-primary transition-colors"
              onClick={toggleMenu}
            >
              About
            </Link>
            <Link
              to="/contact"
              className="block text-xl font-montserrat text-secondary hover:text-primary transition-colors"
              onClick={toggleMenu}
            >
              Contact
            </Link>

            {user ? (
              <>
                <Link
                  to="/dashboard"
                  className="block text-xl font-montserrat text-secondary hover:text-primary transition-colors"
                  onClick={toggleMenu}
                >
                  Dashboard
                </Link>
                <button
                  onClick={() => {
                    handleLogout();
                    toggleMenu();
                  }}
                  className="block w-full text-left text-xl font-montserrat text-secondary hover:text-primary transition-colors"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="block text-xl font-montserrat text-secondary hover:text-primary transition-colors"
                  onClick={toggleMenu}
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="block text-xl font-montserrat text-secondary hover:text-primary transition-colors"
                  onClick={toggleMenu}
                >
                  Register
                </Link>
              </>
            )}

            <div className="pt-4">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleSearch}
                placeholder="Search menu..."
                className="w-full p-4 border border-gray-300 rounded-xl text-lg font-montserrat bg-white/80 backdrop-blur-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition-all"
                aria-label="Search menu"
              />
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}

export default Navbar;